// Geboortevenster · Niveau A: eerste-orde-schatting van de grenzen uit de snelheden in het chartdocument, zonder backend (voedt de indicator en de hints).
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { HdBodygraphChartPreset, HdBodygraphPlanetaryActivation } from '../bodygraph/hd-bodygraph.models';
import { getHdPlanetDisplayConfig } from '../bodygraph/planet-grid/hd-planet-display-config';
import { MANDALA_DEGREES_PER_LINE, MANDALA_GATE_41_START_LONGITUDE } from '../mandala/mandala.constants';
import { activationFromLongitude } from '../mandala/mandala-layout';
import { HdBirthWindowBodySegments, HdBirthWindowData, HdBirthWindowSegment, HdBirthWindowSide } from './birth-window.models';

/**
 * Niveau A (spec §2.3): eerste-orde-schatting van de grensmomenten uit de
 * lengte én snelheid van elke activatie, zonder ephemeris-calls. Levert
 * dezelfde datavorm als de exacte backend-solver, zodat de lanes- en
 * margebouwers er niets van merken (`estimated: true`).
 *
 * Nauwkeurigheid: binnen ±12 u op seconden voor alle lichamen behalve de Maan
 * (daar tot 1-2 min over 6 u); goed genoeg voor de indicator, niet voor het
 * paneel. Een activatie zonder snelheid (oude documenten) maakt de schatting
 * onmogelijk: dan `undefined`, en de aanroeper haalt een vers document op.
 */

const DAY_MS = 86_400_000;
const CELL_COUNT_LINE = 64 * 6;
const CELL_COUNT_TONE = 64 * 6 * 6 * 6;
const TONE_WIDTH = MANDALA_DEGREES_PER_LINE / 36;
/** Onder deze snelheid (°/dag) is een lineaire extrapolatie zinloos: één segment over het venster. */
const MIN_SPEED_DEG_PER_DAY = 1e-4;

const SUN_ID = getHdPlanetDisplayConfig('sun').planetId;
const EARTH_ID = getHdPlanetDisplayConfig('earth').planetId;
const NORTH_NODE_ID = getHdPlanetDisplayConfig('northNode').planetId;
const SOUTH_NODE_ID = getHdPlanetDisplayConfig('southNode').planetId;
const TONE_LEVEL_PLANET_IDS = new Set([SUN_ID, EARTH_ID, NORTH_NODE_ID, SOUTH_NODE_ID]);

type SpeedActivation = HdBodygraphPlanetaryActivation & { longitudeSpeed?: number | null };

function normalizeLongitude(value: number): number {
  const normalized = value % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

function speedOf(activation: SpeedActivation): number | undefined {
  const speed = activation.longitudeSpeed;
  return typeof speed === 'number' && Number.isFinite(speed) ? speed : undefined;
}

function sideOf(activation: HdBodygraphPlanetaryActivation): HdBirthWindowSide | undefined {
  return activation.side === 'personality' || activation.side === 'design' ? activation.side : undefined;
}

function cellSegment(cellIndex: number, cellWidth: number, cellCount: number, tone: boolean, fromMs: number, toMs: number): HdBirthWindowSegment {
  const index = ((cellIndex % cellCount) + cellCount) % cellCount;
  const mid = normalizeLongitude(MANDALA_GATE_41_START_LONGITUDE + (index + 0.5) * cellWidth);
  const activation = activationFromLongitude(mid);
  return tone
    ? { fromMs, toMs, gate: activation.gate, line: activation.line, color: activation.color, tone: activation.tone }
    : { fromMs, toMs, gate: activation.gate, line: activation.line };
}

/**
 * Segmenten van één activatie bij constante (effectieve) snelheid in °/dag,
 * in geboortetijd-coördinaten, geclipt op [fromMs, toMs].
 */
export function estimateBodySegments(
  longitude: number,
  effectiveSpeedDegPerDay: number,
  tone: boolean,
  birthMs: number,
  fromMs: number,
  toMs: number,
): HdBirthWindowSegment[] {
  const cellWidth = tone ? TONE_WIDTH : MANDALA_DEGREES_PER_LINE;
  const cellCount = tone ? CELL_COUNT_TONE : CELL_COUNT_LINE;
  const offset = normalizeLongitude(longitude - MANDALA_GATE_41_START_LONGITUDE);
  const cellIndex = Math.min(Math.floor(offset / cellWidth), cellCount - 1);

  if (Math.abs(effectiveSpeedDegPerDay) < MIN_SPEED_DEG_PER_DAY) {
    return [cellSegment(cellIndex, cellWidth, cellCount, tone, fromMs, toMs)];
  }

  const msPerDegree = DAY_MS / Math.abs(effectiveSpeedDegPerDay);
  const forward = effectiveSpeedDegPerDay > 0;
  const cellStart = cellIndex * cellWidth;
  // Afstand (°) tot de grens in bewegingsrichting en tegen de richting in.
  const aheadDeg = forward ? cellStart + cellWidth - offset : offset - cellStart;
  const behindDeg = cellWidth - aheadDeg;
  const step = forward ? 1 : -1;

  // Grensmomenten in geboortetijd: vooruit (na de geboorte) en achteruit (ervoor).
  const boundaries: { ms: number; cellAfter: number }[] = [];
  let ms = birthMs + aheadDeg * msPerDegree;
  let cell = cellIndex;
  while (ms < toMs) {
    cell += step;
    boundaries.push({ ms, cellAfter: cell });
    ms += cellWidth * msPerDegree;
  }
  ms = birthMs - behindDeg * msPerDegree;
  cell = cellIndex;
  const before: { ms: number; cellAfter: number }[] = [];
  while (ms > fromMs) {
    before.push({ ms, cellAfter: cell });
    cell -= step;
    ms -= cellWidth * msPerDegree;
  }
  before.reverse();
  const all = [...before, ...boundaries];

  const segments: HdBirthWindowSegment[] = [];
  let currentCell = before.length > 0 ? before[0].cellAfter - step : cellIndex;
  let currentFrom = fromMs;
  for (const boundary of all) {
    segments.push(cellSegment(currentCell, cellWidth, cellCount, tone, currentFrom, boundary.ms));
    currentCell = boundary.cellAfter;
    currentFrom = boundary.ms;
  }
  segments.push(cellSegment(currentCell, cellWidth, cellCount, tone, currentFrom, toMs));
  return segments;
}

export function estimateBirthWindowData(preset: HdBodygraphChartPreset, halfWindowMs: number): HdBirthWindowData | undefined {
  const utc = (preset.birthData as { time?: { utc?: string | null } } | undefined)?.time?.utc;
  const birthMs = utc ? Date.parse(utc) : Number.NaN;
  if (!Number.isFinite(birthMs)) return undefined;

  const planetary = (preset.planetaryActivations ?? []) as SpeedActivation[];
  const pSun = planetary.find(a => a.planetId === SUN_ID && a.side === 'personality');
  const dSun = planetary.find(a => a.planetId === SUN_ID && a.side === 'design');
  const pSunSpeed = pSun ? speedOf(pSun) : undefined;
  const dSunSpeed = dSun ? speedOf(dSun) : undefined;
  if (pSunSpeed === undefined || dSunSpeed === undefined || dSunSpeed === 0) return undefined;
  // Het designmoment schuift mee met de geboorte, geschaald op de zonsnelheden (spec §2.2).
  const designRatio = pSunSpeed / dSunSpeed;

  const fromMs = birthMs - halfWindowMs;
  const toMs = birthMs + halfWindowMs;
  const bodies: HdBirthWindowBodySegments[] = [];
  for (const activation of planetary) {
    const side = sideOf(activation);
    if (!side || activation.sourceType !== 'planet') continue;
    const speed = speedOf(activation);
    if (speed === undefined) return undefined;
    const effectiveSpeed = side === 'design' ? speed * designRatio : speed;
    const tone = TONE_LEVEL_PLANET_IDS.has(activation.planetId);
    bodies.push({
      side,
      planetId: activation.planetId,
      sourceKey: activation.sourceKey,
      segments: estimateBodySegments(activation.longitude, effectiveSpeed, tone, birthMs, fromMs, toMs),
    });
  }
  if (bodies.length === 0) return undefined;
  return { birthMs, fromMs, toMs, bodies, estimated: true };
}
